/**
 * Dynamic Theme Generator — Runtime M3 Color Token Engine
 *
 * Generates design system tokens from a single source (brand) color
 * using Material Color Utilities.
 *
 * Architecture:
 *   Source Color → Scheme Variant → Tonal Palettes → System + Component Tokens
 *
 * This module is purely functional — no DOM access, no side-effects.
 * Pair with apply-theme.ts to inject tokens into the page.
 *
 * References:
 *   - https://github.com/material-foundation/material-color-utilities
 *   - https://m3.material.io/styles/color/system/how-the-system-works
 */

import {
    argbFromHex,
    DislikeAnalyzer,
    Hct,
    hexFromArgb,
    TemperatureCache,
    TonalPalette,
} from '@material/material-color-utilities';

// =========================================================================
// Types
// =========================================================================

/** Token map — keys are token names, values are hex strings */
export interface TokenMap {
    [tokenName: string]: string;
}

/** Full output of the generator */
export interface GeneratedTheme {
    system: { light: TokenMap; dark: TokenMap };
    component: { light: TokenMap; dark: TokenMap };
}

/** Options for token generation */
export interface ThemeGeneratorOptions {
    /** Optional secondary color hex (e.g. "#8C62F5") */
    secondaryHex?: string | null;
    /** Scheme variant key — defaults to "fidelity" */
    variant?: SchemeVariantKey;
}

// =========================================================================
// Scheme Variant Definitions
// =========================================================================

interface SchemeVariant {
    name: string;
    primaryHueShift?: number;
    getPrimaryChroma: (sourceChroma: number) => number;
    getSecondaryChroma: (sourceChroma: number) => number;
    getTertiaryHueShift?: number;
    tertiaryChroma?: number;
    neutralChroma?: number;
    neutralChromaDivisor?: number;
    neutralHueShift?: number;
    useTemperatureComplement?: boolean;
    useAnalogousTertiary?: boolean;
}

const SCHEME_VARIANTS: Record<string, SchemeVariant> = {
    fidelity: {
        name: 'Fidelity',
        getPrimaryChroma: c => c,
        getSecondaryChroma: c => Math.max(c - 32, c * 0.5),
        useTemperatureComplement: true,
        neutralChromaDivisor: 8,
    },
    content: {
        name: 'Content',
        getPrimaryChroma: c => c,
        getSecondaryChroma: c => c / 3,
        useAnalogousTertiary: true,
        neutralChromaDivisor: 12,
    },
    tonal_spot: {
        name: 'Tonal Spot',
        getPrimaryChroma: () => 36,
        getSecondaryChroma: () => 16,
        getTertiaryHueShift: 60,
        tertiaryChroma: 24,
        neutralChroma: 6,
    },
    vibrant: {
        name: 'Vibrant',
        getPrimaryChroma: () => 200,
        getSecondaryChroma: () => 24,
        tertiaryChroma: 32,
        neutralChroma: 10,
    },
    expressive: {
        name: 'Expressive',
        primaryHueShift: 240,
        getPrimaryChroma: () => 40,
        getSecondaryChroma: () => 24,
        tertiaryChroma: 32,
        neutralChroma: 8,
        neutralHueShift: 15,
    },
    neutral: {
        name: 'Neutral',
        getPrimaryChroma: () => 12,
        getSecondaryChroma: () => 8,
        getTertiaryHueShift: 60,
        tertiaryChroma: 16,
        neutralChroma: 2,
    },
    monochrome: {
        name: 'Monochrome',
        getPrimaryChroma: () => 0,
        getSecondaryChroma: () => 0,
        tertiaryChroma: 0,
        neutralChroma: 0,
    },
};

export type SchemeVariantKey = keyof typeof SCHEME_VARIANTS;

// =========================================================================
// Static Semantic Color Sources (same across all themes)
// =========================================================================

const SEMANTIC_COLOR_SOURCES: Record<string, string> = {
    red: '#E22B3D',
    purple: '#8C62F5',
    blue: '#2770EF',
    teal: '#48D1E0',
    yellow: '#FCC838',
    green: '#06BF7F',
    orange: '#FF8142',
};

// =========================================================================
// Tone Mapping — Design System Scale → M3 Tone
// =========================================================================

/**
 * Our design system uses scale 00 (lightest) → 100 (darkest).
 * M3 uses tone 0 (darkest) → 100 (lightest).
 * This map bridges the two.
 */
const SCALE_TO_M3_TONE: Record<string, number> = {
    '00': 100,
    '10': 96,
    '20': 90,
    '30': 80,
    '40': 70,
    '50': 60,
    '60': 40,
    '70': 30,
    '80': 20,
    '85': 15,
    '90': 10,
    '100': 0,
};

// =========================================================================
// WCAG-compliant Content Tones
// =========================================================================

const CONTENT_TONES = {
    light: { primary: 10, secondary: 40, tertiary: 50, disabled: 70 },
    dark: { primary: 90, secondary: 60, tertiary: 50, disabled: 40 },
};

const BRAND_TONES = {
    light: { foreground: 40, background: 80 },
    dark: { foreground: 80, background: 40 },
};

// =========================================================================
// Helpers
// =========================================================================

/** Clamp degrees into [0, 360). */
function sanitizeDegrees(degrees: number): number {
    return ((degrees % 360) + 360) % 360;
}

/** Shorthand — look up the M3 tone for a design-system scale. */
function scaleToTone(s: string): number {
    return SCALE_TO_M3_TONE[s];
}

// =========================================================================
// Palette Generation
// =========================================================================

interface Palettes {
    brand: TonalPalette;
    secondary: TonalPalette;
    tertiary: TonalPalette;
    gray: TonalPalette;
    grayVariant: TonalPalette;
    red: TonalPalette;
    purple: TonalPalette;
    blue: TonalPalette;
    teal: TonalPalette;
    yellow: TonalPalette;
    green: TonalPalette;
    orange: TonalPalette;
}

/**
 * Build all tonal palettes from a primary (brand) color.
 *
 * Dynamic palettes (brand, secondary, tertiary, gray) are derived from the
 * source color + scheme variant.  Semantic palettes (red, green, etc.) are
 * static so their meaning stays consistent across themes.
 */
function generateAllPalettes(
    primaryHct: Hct,
    customSecondaryHct: Hct | null,
    variantKey: string,
): Palettes {
    const variant = SCHEME_VARIANTS[variantKey] ?? SCHEME_VARIANTS.fidelity;
    const sourceHue = primaryHct.hue;
    const sourceChroma = primaryHct.chroma;

    // --- Brand palette (dynamic) ---
    const brandHue = variant.primaryHueShift
        ? sanitizeDegrees(sourceHue + variant.primaryHueShift)
        : sourceHue;
    const brandChroma = variant.getPrimaryChroma(sourceChroma);
    const brandPalette = TonalPalette.fromHueAndChroma(brandHue, brandChroma);

    // --- Secondary palette (custom or derived) ---
    let secondaryPalette: TonalPalette;
    if (customSecondaryHct) {
        secondaryPalette = TonalPalette.fromHct(customSecondaryHct);
    } else {
        const secondaryChroma = variant.getSecondaryChroma(sourceChroma);
        secondaryPalette = TonalPalette.fromHueAndChroma(
            sourceHue,
            secondaryChroma,
        );
    }

    // --- Tertiary palette (temperature complement or hue shift) ---
    let tertiaryPalette: TonalPalette;
    if (variant.useTemperatureComplement) {
        const tempCache = new TemperatureCache(primaryHct);
        tertiaryPalette = TonalPalette.fromHct(
            DislikeAnalyzer.fixIfDisliked(tempCache.complement),
        );
    } else if (variant.useAnalogousTertiary) {
        const tempCache = new TemperatureCache(primaryHct);
        tertiaryPalette = TonalPalette.fromHct(
            DislikeAnalyzer.fixIfDisliked(tempCache.analogous(3, 6)[2]),
        );
    } else {
        const tertiaryHue = sanitizeDegrees(
            sourceHue + (variant.getTertiaryHueShift ?? 60),
        );
        const tertiaryChroma = variant.tertiaryChroma ?? 24;
        tertiaryPalette = TonalPalette.fromHueAndChroma(
            tertiaryHue,
            tertiaryChroma,
        );
    }

    // --- Gray / neutral palette (tinted with brand hue) ---
    let neutralHue: number;
    let neutralChroma: number;
    if (customSecondaryHct) {
        neutralHue = customSecondaryHct.hue;
        neutralChroma = Math.max(2, Math.min(8, customSecondaryHct.chroma / 8));
    } else {
        neutralHue = variant.neutralHueShift
            ? sanitizeDegrees(sourceHue + variant.neutralHueShift)
            : sourceHue;
        if (variant.neutralChroma !== undefined) {
            neutralChroma = variant.neutralChroma;
        } else if (variant.neutralChromaDivisor) {
            neutralChroma = sourceChroma / variant.neutralChromaDivisor;
        } else {
            neutralChroma = 4;
        }
    }

    const grayPalette = TonalPalette.fromHueAndChroma(
        neutralHue,
        neutralChroma,
    );
    const grayVariantPalette = TonalPalette.fromHueAndChroma(
        neutralHue,
        neutralChroma + 4,
    );

    // --- Semantic palettes (static — same across all themes) ---
    const red = TonalPalette.fromInt(argbFromHex(SEMANTIC_COLOR_SOURCES.red));
    const purple = TonalPalette.fromInt(
        argbFromHex(SEMANTIC_COLOR_SOURCES.purple),
    );
    const blue = TonalPalette.fromInt(argbFromHex(SEMANTIC_COLOR_SOURCES.blue));
    const teal = TonalPalette.fromInt(argbFromHex(SEMANTIC_COLOR_SOURCES.teal));
    const yellow = TonalPalette.fromInt(
        argbFromHex(SEMANTIC_COLOR_SOURCES.yellow),
    );
    const green = TonalPalette.fromInt(
        argbFromHex(SEMANTIC_COLOR_SOURCES.green),
    );
    const orange = TonalPalette.fromInt(
        argbFromHex(SEMANTIC_COLOR_SOURCES.orange),
    );

    return {
        brand: brandPalette,
        secondary: secondaryPalette,
        tertiary: tertiaryPalette,
        gray: grayPalette,
        grayVariant: grayVariantPalette,
        red,
        purple,
        blue,
        teal,
        yellow,
        green,
        orange,
    };
}

// =========================================================================
// System Token Generation
// =========================================================================

/**
 * Generate the 43 system tokens (Background / Content / Border) for one mode.
 * Values are returned as hex strings, ready for CSS injection.
 */
function generateSystemModeTokens(
    palettes: Palettes,
    isDark: boolean,
    sourceArgb: number,
): TokenMap {
    const { brand, gray, grayVariant, red, green, yellow, blue } = palettes;

    // Helper — convert ARGB int → hex string
    const hex = (argb: number) => hexFromArgb(argb);

    return {
        // ================================================================
        // BACKGROUND (16 tokens)
        // ================================================================
        'background-sunken': hex(
            isDark ? gray.tone(7) : gray.tone(scaleToTone('00')),
        ),
        'background-base': hex(
            isDark ? gray.tone(10) : gray.tone(scaleToTone('00')),
        ),
        'background-on-base': hex(
            isDark ? gray.tone(10) : gray.tone(scaleToTone('10')),
        ),
        'background-raised': hex(
            isDark ? gray.tone(15) : gray.tone(scaleToTone('00')),
        ),
        'background-active': hex(
            isDark ? gray.tone(6) : gray.tone(scaleToTone('10')),
        ),
        'background-action': hex(
            isDark ? gray.tone(20) : gray.tone(scaleToTone('20')),
        ),
        // Light mode: exact source color for brand backgrounds
        // Dark mode: lighter tonal variant for visibility
        'background-brand': isDark
            ? hex(brand.tone(80))
            : hexFromArgb(sourceArgb),
        'background-overlay': hex(
            isDark ? gray.tone(90) : gray.tone(scaleToTone('70')),
        ),
        'background-base-inverse': hex(
            isDark ? gray.tone(90) : gray.tone(scaleToTone('90')),
        ),
        'background-raised-inverse': hex(
            isDark ? gray.tone(96) : gray.tone(15),
        ),
        'background-success': hex(
            isDark ? green.tone(30) : green.tone(scaleToTone('20')),
        ),
        'background-warning': hex(
            isDark ? yellow.tone(30) : yellow.tone(scaleToTone('20')),
        ),
        'background-failure': hex(
            isDark ? red.tone(30) : red.tone(scaleToTone('20')),
        ),
        // background-information uses brand palette — 95% of usages are brand-selected
        // states (buttons, menus, dates, lists). M3-correct: info = primary container.
        'background-information': hex(
            isDark ? blue.tone(30) : blue.tone(scaleToTone('20')),
        ),
        'background-ghost-hover': hex(
            isDark ? gray.tone(30) : gray.tone(scaleToTone('10')),
        ),
        // Brand-tinted ghost highlight — uses brand palette so it adapts
        // to the custom source color. Tones approximate the alpha blend
        // on expected backgrounds (dark bg / white bg).
        'background-ghost-highlight': hex(
            isDark ? brand.tone(25) : brand.tone(90),
        ),

        // ================================================================
        // CONTENT (15 tokens)
        // ================================================================
        'content-primary': hex(
            isDark
                ? gray.tone(CONTENT_TONES.dark.primary)
                : gray.tone(CONTENT_TONES.light.primary),
        ),
        'content-secondary': hex(
            isDark
                ? gray.tone(CONTENT_TONES.dark.secondary)
                : gray.tone(CONTENT_TONES.light.secondary),
        ),
        'content-tertiary': hex(
            isDark
                ? gray.tone(CONTENT_TONES.dark.tertiary)
                : gray.tone(CONTENT_TONES.light.tertiary),
        ),
        'content-alternate': hex(
            isDark ? gray.tone(10) : gray.tone(scaleToTone('00')),
        ),
        'content-brand': hex(
            isDark
                ? brand.tone(BRAND_TONES.dark.foreground)
                : brand.tone(BRAND_TONES.light.foreground),
        ),
        'content-primary-inverse': hex(
            isDark ? gray.tone(10) : gray.tone(scaleToTone('00')),
        ),
        'content-alternate-inverse': hex(
            isDark ? gray.tone(90) : gray.tone(scaleToTone('90')),
        ),
        'content-brand-inverse': hex(
            isDark
                ? brand.tone(BRAND_TONES.light.foreground)
                : brand.tone(BRAND_TONES.dark.foreground),
        ),
        'content-success': hex(
            isDark ? green.tone(70) : green.tone(scaleToTone('60')),
        ),
        'content-warning': hex(
            isDark ? yellow.tone(70) : yellow.tone(scaleToTone('60')),
        ),
        'content-failure': hex(
            isDark ? red.tone(70) : red.tone(scaleToTone('60')),
        ),
        'content-information': hex(
            isDark ? blue.tone(70) : blue.tone(scaleToTone('60')),
        ),
        'content-link': hex(
            isDark
                ? brand.tone(BRAND_TONES.dark.foreground)
                : brand.tone(BRAND_TONES.light.foreground),
        ),
        'content-link-inverse': hex(
            isDark ? gray.tone(90) : gray.tone(scaleToTone('00')),
        ),
        'content-link-inverse-hover': hex(
            isDark ? gray.tone(80) : gray.tone(scaleToTone('20')),
        ),

        // ================================================================
        // BORDER (12 tokens)
        // ================================================================
        'border-default': hex(
            isDark ? grayVariant.tone(50) : gray.tone(scaleToTone('40')),
        ),
        'border-hover': hex(
            isDark ? grayVariant.tone(60) : gray.tone(scaleToTone('50')),
        ),
        'border-divider': hex(
            isDark ? grayVariant.tone(30) : gray.tone(scaleToTone('20')),
        ),
        'border-brand': hex(isDark ? brand.tone(70) : brand.tone(40)),
        'border-warning': hex(
            isDark ? yellow.tone(70) : yellow.tone(scaleToTone('60')),
        ),
        'border-failure': hex(
            isDark ? red.tone(70) : red.tone(scaleToTone('60')),
        ),
        'border-default-inverse': hex(
            isDark ? grayVariant.tone(70) : gray.tone(scaleToTone('70')),
        ),
        'border-hover-inverse': hex(
            isDark ? grayVariant.tone(60) : gray.tone(scaleToTone('60')),
        ),
        'border-divider-inverse': hex(
            isDark ? grayVariant.tone(80) : gray.tone(scaleToTone('80')),
        ),
        'border-brand-inverse': hex(
            isDark ? brand.tone(40) : brand.tone(scaleToTone('50')),
        ),
        'border-focus': hex(
            isDark ? brand.tone(60) : brand.tone(scaleToTone('50')),
        ),
        'border-gap': hex(
            isDark ? gray.tone(10) : gray.tone(scaleToTone('00')),
        ),
    };
}

// =========================================================================
// Component Token Generation
// =========================================================================

/**
 * Generate component tokens (Button, Chip, Toggle) for one mode.
 * Values are returned as hex strings.
 */
function generateComponentModeTokens(
    palettes: Palettes,
    isDark: boolean,
    sourceArgb: number,
): TokenMap {
    const { brand, gray, red, purple, green, blue } = palettes;
    const hex = (argb: number) => hexFromArgb(argb);

    return {
        // ================================================================
        // BUTTON
        // ================================================================
        // Primary: exact source in light; lighter tonal in dark
        'button-primary-default': isDark
            ? hex(brand.tone(80))
            : hexFromArgb(sourceArgb),
        'button-primary-hover': hex(isDark ? brand.tone(70) : brand.tone(30)),
        'button-primary-pressed': hex(isDark ? brand.tone(60) : brand.tone(20)),
        'button-primary-on-color-default': hex(
            isDark ? gray.tone(10) : gray.tone(scaleToTone('00')),
        ),
        // On-color uses brand palette so hover/pressed tint matches the brand
        'button-primary-on-color-hover': hex(
            isDark ? brand.tone(90) : brand.tone(scaleToTone('20')),
        ),
        'button-primary-on-color-pressed': hex(
            isDark ? brand.tone(80) : brand.tone(scaleToTone('30')),
        ),
        'button-secondary-default': hex(
            isDark ? gray.tone(30) : gray.tone(scaleToTone('20')),
        ),
        'button-secondary-hover': hex(
            isDark ? gray.tone(40) : gray.tone(scaleToTone('30')),
        ),
        // Secondary pressed has a brand tint
        'button-secondary-pressed': hex(
            isDark ? brand.tone(30) : brand.tone(scaleToTone('20')),
        ),
        'button-tertiary-hover': hex(
            isDark ? gray.tone(30) : gray.tone(scaleToTone('40')),
        ),
        // Tertiary pressed has a brand-tinted highlight
        'button-tertiary-pressed': hex(
            isDark ? brand.tone(30) : brand.tone(scaleToTone('50')),
        ),

        // ================================================================
        // CHIP – Loading
        // ================================================================
        'chip-loading-default': hex(
            isDark ? gray.tone(20) : gray.tone(scaleToTone('10')),
        ),

        // ================================================================
        // CHIP – Attribute (Blue — fixed semantic, NOT brand-aware)
        // Attribute chips = data type indicator (like measure=green, date=purple)
        // ================================================================
        'chip-attribute-default': hex(
            isDark ? blue.tone(30) : blue.tone(scaleToTone('20')),
        ),
        'chip-attribute-hover': hex(
            isDark ? blue.tone(40) : blue.tone(scaleToTone('30')),
        ),
        'chip-attribute-pressed': hex(
            isDark ? blue.tone(50) : blue.tone(scaleToTone('40')),
        ),
        'chip-attribute-icon': hex(
            isDark ? blue.tone(80) : blue.tone(scaleToTone('60')),
        ),
        'chip-attribute-active-border': hex(
            isDark ? blue.tone(50) : blue.tone(scaleToTone('30')),
        ),

        // ================================================================
        // CHIP – Measure (Green)
        // ================================================================
        'chip-measure-default': hex(
            isDark ? green.tone(30) : green.tone(scaleToTone('20')),
        ),
        'chip-measure-hover': hex(
            isDark ? green.tone(40) : green.tone(scaleToTone('30')),
        ),
        'chip-measure-pressed': hex(
            isDark ? green.tone(50) : green.tone(scaleToTone('40')),
        ),
        'chip-measure-icon': hex(
            isDark ? green.tone(80) : green.tone(scaleToTone('60')),
        ),
        'chip-measure-active-border': hex(
            isDark ? green.tone(50) : green.tone(scaleToTone('30')),
        ),

        // ================================================================
        // CHIP – Filter (Gray)
        // ================================================================
        'chip-filter-default': hex(
            isDark ? gray.tone(30) : gray.tone(scaleToTone('20')),
        ),
        'chip-filter-hover': hex(
            isDark ? gray.tone(40) : gray.tone(scaleToTone('30')),
        ),
        'chip-filter-pressed': hex(
            isDark ? gray.tone(50) : gray.tone(scaleToTone('40')),
        ),
        'chip-filter-icon': hex(
            isDark ? gray.tone(60) : gray.tone(scaleToTone('60')),
        ),
        'chip-filter-active-border': hex(
            isDark ? gray.tone(50) : gray.tone(scaleToTone('30')),
        ),

        // ================================================================
        // CHIP – Date (Purple)
        // ================================================================
        'chip-date-default': hex(
            isDark ? purple.tone(30) : purple.tone(scaleToTone('20')),
        ),
        'chip-date-hover': hex(
            isDark ? purple.tone(40) : purple.tone(scaleToTone('30')),
        ),
        'chip-date-pressed': hex(
            isDark ? purple.tone(50) : purple.tone(scaleToTone('40')),
        ),
        'chip-date-icon': hex(
            isDark ? purple.tone(80) : purple.tone(scaleToTone('60')),
        ),
        'chip-date-active-border': hex(
            isDark ? purple.tone(50) : purple.tone(scaleToTone('30')),
        ),

        // ================================================================
        // CHIP – Input
        // ================================================================
        'chip-input-default': hex(
            isDark ? gray.tone(20) : gray.tone(scaleToTone('00')),
        ),
        'chip-input-hover': hex(
            isDark ? gray.tone(30) : gray.tone(scaleToTone('10')),
        ),
        'chip-input-pressed': hex(
            isDark ? gray.tone(40) : gray.tone(scaleToTone('20')),
        ),
        'chip-input-selected-default': hex(
            isDark ? gray.tone(80) : gray.tone(scaleToTone('70')),
        ),
        'chip-input-selected-hover': hex(
            isDark ? gray.tone(85) : gray.tone(scaleToTone('80')),
        ),
        'chip-input-selected-pressed': hex(
            isDark ? gray.tone(90) : gray.tone(scaleToTone('90')),
        ),

        // ================================================================
        // CHIP – Error (Red)
        // ================================================================
        'chip-error-default': hex(
            isDark ? red.tone(30) : red.tone(scaleToTone('20')),
        ),
        'chip-error-hover': hex(
            isDark ? red.tone(40) : red.tone(scaleToTone('30')),
        ),
        'chip-error-active-border': hex(
            isDark ? gray.tone(50) : gray.tone(scaleToTone('30')),
        ),

        // ================================================================
        // TOGGLE
        // ================================================================
        'toggle-track-off': hex(
            isDark ? gray.tone(50) : gray.tone(scaleToTone('40')),
        ),
        'toggle-track-on': hex(
            isDark ? brand.tone(50) : brand.tone(scaleToTone('30')),
        ),
        'toggle-thumb-off': hex(
            isDark ? gray.tone(90) : gray.tone(scaleToTone('00')),
        ),
        'toggle-thumb-on': hex(
            isDark ? brand.tone(80) : brand.tone(scaleToTone('60')),
        ),
        'toggle-thumb-outline-off': hex(
            isDark ? gray.tone(60) : gray.tone(scaleToTone('30')),
        ),
    };
}

// =========================================================================
// Public API
// =========================================================================

/**
 * Generate all design system tokens from a single source (brand) color.
 *
 * Returns system and component token maps for both light and dark modes.
 * Every value is a hex string (e.g., "#2770ef").
 *
 * @param primaryHex - Source/brand color in hex (e.g., "#2770ef")
 * @param options    - Optional secondary color and scheme variant
 * @returns          - Complete token maps for both modes
 *
 * @example
 * ```ts
 * const theme = generateTheme('#2770ef');
 * console.log(theme.system.light['background-brand']); // "#2770ef"
 * console.log(theme.system.dark['background-brand']);   // lighter variant
 * ```
 */
export function generateTheme(
    primaryHex: string,
    options: ThemeGeneratorOptions = {},
): GeneratedTheme {
    const { secondaryHex = null, variant = 'fidelity' } = options;

    const primaryArgb = argbFromHex(primaryHex);
    const primaryHct = Hct.fromInt(primaryArgb);

    // Parse optional secondary color
    let customSecondaryHct: Hct | null = null;
    if (secondaryHex) {
        customSecondaryHct = Hct.fromInt(argbFromHex(secondaryHex));
    }

    // Build tonal palettes from source color + variant
    const palettes = generateAllPalettes(
        primaryHct,
        customSecondaryHct,
        variant,
    );

    // Generate tokens for both light and dark modes
    return {
        system: {
            light: generateSystemModeTokens(palettes, false, primaryArgb),
            dark: generateSystemModeTokens(palettes, true, primaryArgb),
        },
        component: {
            light: generateComponentModeTokens(palettes, false, primaryArgb),
            dark: generateComponentModeTokens(palettes, true, primaryArgb),
        },
    };
}
